import React from 'react';
import { Link } from 'react-router-dom';
import { Anchor, Shield, TrendingUp, Users, Phone, Mail, MapPin, Linkedin } from 'lucide-react';
import { usePageTitle } from '../contexts/PageTitleContext';

export default function AboutUs() {
  usePageTitle().setTitle('Chi Siamo - Sea Level Traders');
  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #0c1929 0%, #0f2942 50%, #0c1929 100%)' }}>
      {/* Hero */}
      <section className="relative py-20 px-4 text-center">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 text-sm font-medium"
               style={{ background: 'rgba(6,182,212,0.15)', color: '#22d3ee', border: '1px solid rgba(6,182,212,0.3)' }}>
            <Shield size={16} />
            Chi Siamo
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ lineHeight: '1.15' }}>
            Sea Level Traders
            <span className="block" style={{ color: '#22d3ee' }}>Marketplace B2B per Dealer Nautici</span>
          </h1>
          <p className="text-lg md:text-xl max-w-3xl mx-auto" style={{ color: 'rgba(255,255,255,0.7)' }}>
            La prima piattaforma italiana dedicata esclusivamente alla compravendita di barche usate tra concessionari, broker e cantieri certificati.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl p-8 md:p-12" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">La Nostra Missione</h2>
            <p className="text-base md:text-lg mb-6" style={{ color: 'rgba(255,255,255,0.7)', lineHeight: '1.7' }}>
              Il mercato B2B nautico italiano muove oltre <strong style={{ color: '#fbbf24' }}>€800 milioni l'anno</strong> con circa 12.000 unità scambiate tra professionisti del settore. Eppure, dealer e broker hanno sempre dipeso da canali frammentati: portali generalisti, passaparola, fiere locali.
            </p>
            <p className="text-base md:text-lg mb-6" style={{ color: 'rgba(255,255,255,0.7)', lineHeight: '1.7' }}>
              Sea Level Traders nasce per risolvere questo problema: un marketplace esclusivo dove concessionari, broker e cantieri possono scambiare stock, negoziare prezzi B2B e gestire l'intero ciclo di compravendita in un ambiente certificato e trasparente.
            </p>
            <p className="text-base md:text-lg" style={{ color: 'rgba(255,255,255,0.7)', lineHeight: '1.7' }}>
              Non siamo un portale per privati. Non permettiamo inserzioni non verificate. Il nostro unico obiettivo è far incontrare professionisti del settore nautico per ridurre i tempi di vendita, aumentare i margini e creare una rete nazionale affidabile.
            </p>
          </div>
        </div>
      </section>

      {/* Differenziatori */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Perché Sea Level Traders</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                title: 'Solo Dealer Certificati',
                desc: 'Partita IVA attiva, assicurazione RC professionale, certificazioni di settore. Nessun privato, nessuna inserzione non verificata.'
              },
              {
                icon: TrendingUp,
                title: 'Prezzi B2B Reali',
                desc: 'Trasparenza totale: i prezzi sono negoziati tra dealer, con margini realistici. Niente markup nascosti, niente sorprese.'
              },
              {
                icon: Users,
                title: 'Rete Nazionale',
                desc: 'Dealer in tutta Italia e Mediterraneo. Stock sharing, scambio barche, trattative veloci con professionisti verificati.'
              },
              {
                icon: Anchor,
                title: 'Focus Nautico',
                desc: 'Solo barche e motori marini. Nessuna categoria generica, nessun annuncio fuori contesto. Dati tecnici completi per ogni imbarcazione.'
              },
              {
                icon: Phone,
                title: 'Supporto Dedicato',
                desc: 'Account manager per i dealer, assistenza telefonica e via email. Gestione pratiche, contratti e documentazione inclusa.'
              },
              {
                icon: Linkedin,
                title: 'Community B2B',
                desc: 'Eventi esclusivi, webinar sul mercato, report trimestrali. Non solo una piattaforma: una community di professionisti.'
              }
            ].map((item, i) => (
              <div key={i} className="rounded-2xl p-6"
                   style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(rgba(255,255,255,0.1)' }}>
                <item.icon className="mb-4" size={28} style={{ color: '#22d3ee' }} />
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', lineHeight: '1.6' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Unisciti alla Rete Dealer</h2>
          <p className="text-lg mb-8" style={{ color: 'rgba(255,255,255,0.7)' }}>
            Se sei un concessionario, broker o cantiere nautico, Sea Level Traders è il tuo canale di vendita B2B esclusivo.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register" className="px-8 py-3 rounded-xl font-semibold text-white text-lg transition-all"
                  style={{ background: 'linear-gradient(135deg, #06b6d4, #0891b2)', boxShadow: '0 4px 20px rgba(6,182,212,0.3)' }}>
              Diventa Dealer
            </Link>
            <Link to="/boats" className="px-8 py-3 rounded-xl font-semibold text-lg transition-all"
                  style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}>
              Esplora Stock B2B
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
